import React, { useState, useEffect } from "react";
import { Modal, Tab, Tabs } from "react-bootstrap";
import {
  usePersonaReaction,
  usePersonaVariants,
  PersonaReaction,
  PersonaVariant,
} from "../../../../hooks/usePersonaSimulation";

interface Props {
  show: boolean;
  handleClose: () => void;
  projectId: string;
  projectTitle: string;
}

export const PersonaTestModal: React.FC<Props> = ({
  show,
  handleClose,
  projectId,
  projectTitle,
}) => {
  const [activeTab, setActiveTab] = useState("reaction");
  const [contentToTest, setContentToTest] = useState("");
  const [contentType, setContentType] = useState("social post");
  const [variantContent, setVariantContent] = useState("");
  const [variantContentType, setVariantContentType] = useState("social post");

  const {
    isLoading: isReacting,
    error: reactionError,
    reaction,
    simulateReaction,
    clearError: clearReactionError,
    reset: resetReaction,
  } = usePersonaReaction();

  const {
    isLoading: isGeneratingVariants,
    error: variantsError,
    variants,
    generateVariants,
    clearError: clearVariantsError,
    reset: resetVariants,
  } = usePersonaVariants();

  useEffect(() => {
    if (show) {
      resetReaction();
      resetVariants();
      setContentToTest("");
      setVariantContent("");
      setActiveTab("reaction");
    }
  }, [show, resetReaction, resetVariants]);

  const handleTestReaction = async () => {
    await simulateReaction(projectId, contentToTest, contentType);
  };

  const handleGenerateVariants = async () => {
    await generateVariants(projectId, variantContent, variantContentType);
  };

  const getVerdictBadge = (verdict: string) => {
    const config: Record<string, { color: string; icon: string }> = {
      strong: { color: "success", icon: "bi-hand-thumbs-up-fill" },
      moderate: { color: "warning", icon: "bi-hand-thumbs-up" },
      weak: { color: "danger", icon: "bi-hand-thumbs-down" },
    };
    const match = config[verdict] || config.moderate;
    const color = match!.color;
    const icon = match!.icon;
    return (
      <span className={`badge bg-${color} fs-7 py-2 px-3`}>
        <i className={`bi ${icon} me-1`}></i>
        {verdict.charAt(0).toUpperCase() + verdict.slice(1)}
      </span>
    );
  };

  const getScoreColor = (score: number) => {
    if (score >= 8) return "success";
    if (score >= 5) return "warning";
    return "danger";
  };

  const error = reactionError || variantsError;
  const clearError = reactionError ? clearReactionError : clearVariantsError;

  const contentTypeOptions = [
    "social post",
    "blog post",
    "email",
    "ad copy",
    "landing page",
    "video script",
    "newsletter",
  ];

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      backdrop="static"
      scrollable
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bolder">
          <i className="bi bi-person-check me-2 text-primary"></i>
          Test with Persona
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-3">
        {error && (
          <div className="alert alert-danger mb-4 d-flex align-items-center">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <span className="flex-grow-1">{error}</span>
            <button
              type="button"
              className="btn-close"
              onClick={clearError}
            ></button>
          </div>
        )}

        <div className="bg-light-primary rounded p-3 mb-4">
          <div className="d-flex align-items-center">
            <i className="bi bi-info-circle text-primary me-2"></i>
            <span className="text-gray-700 fs-7">
              Simulates how your Step 5 persona would react to marketing content.
              Powered by TinyTroupe-inspired character prompting for authentic reactions.
            </span>
          </div>
        </div>

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => k && setActiveTab(k)}
          className="mb-4"
        >
          {/* Test Reaction Tab */}
          <Tab eventKey="reaction" title="Test Reaction">
            <div className="py-3">
              {!reaction ? (
                <>
                  <div className="mb-4">
                    <label className="form-label fw-bold">Content Type</label>
                    <div className="d-flex flex-wrap gap-2">
                      {contentTypeOptions.map((type) => (
                        <button
                          key={type}
                          className={`btn btn-sm ${
                            contentType === type ? "btn-primary" : "btn-light"
                          }`}
                          onClick={() => setContentType(type)}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold">
                      Paste your marketing content
                    </label>
                    <textarea
                      className="form-control"
                      rows={8}
                      placeholder={`Paste the ${contentType} you want to test against your persona...`}
                      value={contentToTest}
                      onChange={(e) => setContentToTest(e.target.value)}
                    />
                    <div className="form-text">
                      Paste any marketing content — a social post, email subject line, blog intro,
                      ad copy, or landing page text.
                    </div>
                  </div>

                  <button
                    className="btn btn-primary btn-lg px-5"
                    onClick={handleTestReaction}
                    disabled={isReacting || !contentToTest.trim()}
                  >
                    {isReacting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Simulating Persona Reaction...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-person-check me-2"></i>
                        Test with Persona
                      </>
                    )}
                  </button>
                </>
              ) : (
                <ReactionResults
                  reaction={reaction}
                  getVerdictBadge={getVerdictBadge}
                  getScoreColor={getScoreColor}
                  onReset={() => {
                    resetReaction();
                    setContentToTest("");
                  }}
                />
              )}
            </div>
          </Tab>

          {/* Generate Variants Tab */}
          <Tab eventKey="variants" title="Generate Variants">
            <div className="py-3">
              {variants.length === 0 ? (
                <>
                  <div className="mb-4">
                    <label className="form-label fw-bold">Content Type</label>
                    <div className="d-flex flex-wrap gap-2">
                      {contentTypeOptions.map((type) => (
                        <button
                          key={type}
                          className={`btn btn-sm ${
                            variantContentType === type
                              ? "btn-primary"
                              : "btn-light"
                          }`}
                          onClick={() => setVariantContentType(type)}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold">
                      Paste content to create variants of
                    </label>
                    <textarea
                      className="form-control"
                      rows={8}
                      placeholder={`Paste the ${variantContentType} you want persona-optimized alternatives for...`}
                      value={variantContent}
                      onChange={(e) => setVariantContent(e.target.value)}
                    />
                    <div className="form-text">
                      We'll generate 3 alternative versions, each targeting a different
                      psychological angle of your persona.
                    </div>
                  </div>

                  <button
                    className="btn btn-primary btn-lg px-5"
                    onClick={handleGenerateVariants}
                    disabled={isGeneratingVariants || !variantContent.trim()}
                  >
                    {isGeneratingVariants ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Generating Variants...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-layers me-2"></i>
                        Generate Persona Variants
                      </>
                    )}
                  </button>
                </>
              ) : (
                <VariantsResults
                  variants={variants}
                  onReset={() => {
                    resetVariants();
                    setVariantContent("");
                  }}
                />
              )}
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <button
          type="button"
          className="btn btn-light"
          onClick={handleClose}
          disabled={isReacting || isGeneratingVariants}
        >
          {reaction || variants.length > 0 ? "Done" : "Cancel"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

// Reaction Results Sub-component
const ReactionResults: React.FC<{
  reaction: PersonaReaction;
  getVerdictBadge: (verdict: string) => JSX.Element;
  getScoreColor: (score: number) => string;
  onReset: () => void;
}> = ({ reaction, getVerdictBadge, getScoreColor, onReset }) => {
  const scoreColor = getScoreColor(reaction.relevanceScore);

  return (
    <div>
      {/* Top Score + Verdict */}
      <div className="row g-4 mb-4">
        <div className="col-md-4">
          <div className="card border-0 bg-light text-center py-4">
            <div className={`fs-1 fw-bolder text-${scoreColor}`}>
              {reaction.relevanceScore}/10
            </div>
            <div className="text-muted fs-7 mb-2">Relevance Score</div>
            <div className="progress mx-auto" style={{ height: "8px", width: "120px" }}>
              <div
                className={`progress-bar bg-${scoreColor}`}
                style={{ width: `${reaction.relevanceScore * 10}%` }}
              ></div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 bg-light text-center py-4">
            <div className="mb-2">{getVerdictBadge(reaction.overallVerdict)}</div>
            <div className="text-muted fs-7">Overall Verdict</div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 bg-light text-center py-4">
            <i className="bi bi-cursor-fill text-primary fs-2 mb-1"></i>
            <div className="fw-bold fs-7">{reaction.likelyAction}</div>
            <div className="text-muted fs-7">Likely Action</div>
          </div>
        </div>
      </div>

      {/* Emotional Response */}
      <div className="card border-0 bg-light-primary mb-4">
        <div className="card-body">
          <h6 className="fw-bold text-primary mb-3">
            <i className="bi bi-chat-heart me-2"></i>
            Persona's Inner Monologue
          </h6>
          <p className="mb-0 text-gray-700 fst-italic">
            "{reaction.emotionalResponse}"
          </p>
        </div>
      </div>

      {/* Attention Grabber */}
      <div className="card border-0 bg-light-success mb-4">
        <div className="card-body">
          <h6 className="fw-bold text-success mb-2">
            <i className="bi bi-eye me-2"></i>
            What Caught Their Eye
          </h6>
          <p className="mb-0 text-gray-700">{reaction.attentionGrabber}</p>
        </div>
      </div>

      {/* Objections + Improvements */}
      <div className="row g-4 mb-4">
        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-body">
              <h6 className="fw-bold text-danger mb-3">
                <i className="bi bi-shield-exclamation me-2"></i>
                Objections
              </h6>
              {reaction.objections.length > 0 ? (
                <ul className="list-unstyled mb-0">
                  {reaction.objections.map((obj, i) => (
                    <li key={i} className="mb-2 fs-7">
                      <i className="bi bi-x-circle text-danger me-2"></i>
                      {obj}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted fs-7 mb-0">No major objections</p>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-body">
              <h6 className="fw-bold text-success mb-3">
                <i className="bi bi-lightbulb me-2"></i>
                Improvement Suggestions
              </h6>
              {reaction.improvements.length > 0 ? (
                <ul className="list-unstyled mb-0">
                  {reaction.improvements.map((imp, i) => (
                    <li key={i} className="mb-2 fs-7">
                      <i className="bi bi-arrow-up-circle text-success me-2"></i>
                      {imp}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted fs-7 mb-0">Content is well-optimized</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <button className="btn btn-light-primary" onClick={onReset}>
        <i className="bi bi-arrow-clockwise me-2"></i>
        Test Another Piece of Content
      </button>
    </div>
  );
};

// Variants Results Sub-component
const VariantsResults: React.FC<{
  variants: PersonaVariant[];
  onReset: () => void;
}> = ({ variants, onReset }) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleCopy = (content: string, index: number) => {
    navigator.clipboard.writeText(content);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const angleColors = ["primary", "success", "warning", "info"];

  return (
    <div>
      <div className="row g-4 mb-4">
        {variants.map((variant, index) => (
          <div key={index} className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <span
                      className={`badge bg-light-${
                        angleColors[index % angleColors.length]
                      } text-${angleColors[index % angleColors.length]} fs-7 py-2 px-3`}
                    >
                      <i className="bi bi-lightning me-1"></i>
                      {variant.angle}
                    </span>
                  </div>
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => handleCopy(variant.content, index)}
                  >
                    {copiedIndex === index ? (
                      <>
                        <i className="bi bi-check2 text-success me-1"></i>
                        Copied
                      </>
                    ) : (
                      <>
                        <i className="bi bi-clipboard me-1"></i>
                        Copy
                      </>
                    )}
                  </button>
                </div>

                <div
                  className="bg-light rounded p-3 mb-3 fs-7"
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {variant.content}
                </div>

                <div className="d-flex align-items-start">
                  <i className="bi bi-chat-square-text text-muted me-2 mt-1"></i>
                  <span className="text-muted fs-7 fst-italic">
                    {variant.rationale}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button className="btn btn-light-primary" onClick={onReset}>
        <i className="bi bi-arrow-clockwise me-2"></i>
        Generate More Variants
      </button>
    </div>
  );
};

export default PersonaTestModal;
